import { supabase } from '@/lib/supabase';
import { BaseService } from './baseService';
import { Family, Parent, Student } from '@/types/models';
import { mapArrayToType, mapToFamily, mapToParent, mapToStudent } from '@/utils/supabaseTypeMappers';
import { ErrorCategory } from '@/utils/errorHandler';

/**
 * Service for family-related data operations
 */
export class FamilyService extends BaseService {
  /**
   * Get all families for the current organization
   */
  static async getFamilies(organizationId: string): Promise<Family[]> {
    try {
      const { data, error } = await supabase
        .from('families')
        .select('*')
        .eq('organization_id', organizationId);

      if (error) throw error;
      
      return mapArrayToType(data, mapToFamily);
    } catch (error) {
      this.handleApiError(
        error, 
        'Error fetching families', 
        { 
          category: ErrorCategory.DATABASE,
          operation: 'getFamilies',
          additionalInfo: { organizationId }
        }
      );
      return []; // This line is never reached due to throw in handleApiError, but needed for TypeScript
    }
  }

  /**
   * Add a new family
   */
  static async addFamily(family: Omit<Family, 'id' | 'created_at'>): Promise<Family | null> {
    try {
      const { error } = await supabase
        .from('families')
        .insert([family]);

      if (error) throw error;
      // Do not return the inserted family, let the caller refetch
      return null;
    } catch (error: any) {
      // Check if the error is a constraint violation (duplicate family)
      if (error?.code === '23505') {
        this.handleApiError(
          error, 
          'Family with this name already exists', 
          { 
            category: ErrorCategory.DATABASE,
            operation: 'addFamily',
            additionalInfo: { family }
          }
        );
      } else {
        this.handleApiError(
          error, 
          'Error adding family', 
          { 
            category: ErrorCategory.DATABASE,
            operation: 'addFamily',
            additionalInfo: { family }
          }
        );
      }
      throw new Error('Error adding family');
    }
  }

  /**
   * Get parents by family ID
   */
  static async getParentsByFamily(familyId: string): Promise<Parent[]> {
    try {
      const { data, error } = await supabase
        .from('parents')
        .select('*')
        .eq('family_id', familyId);

      if (error) throw error;
      
      return mapArrayToType(data, mapToParent);
    } catch (error) {
      this.handleApiError(
        error, 
        'Error fetching parents by family', 
        { 
          category: ErrorCategory.DATABASE,
          operation: 'getParentsByFamily',
          additionalInfo: { familyId }
        }
      );
      return [];
    }
  }

  /**
   * Get students by family ID
   */
  static async getStudentsByFamily(familyId: string): Promise<Student[]> {
    try {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .eq('family_id', familyId);

      if (error) throw error;
      
      return mapArrayToType(data, mapToStudent);
    } catch (error) {
      this.handleApiError(
        error, 
        'Error fetching students by family',
        { 
          category: ErrorCategory.DATABASE,
          operation: 'getStudentsByFamily',
          additionalInfo: { familyId }
        }
      );
      return [];
    }
  }

  /**
   * Add a parent to a family
   */
  static async addParent(parent: Omit<Parent, 'id' | 'created_at'>): Promise<Parent> {
    try {
      // If this parent is being set as primary, first update existing primary parents
      if (parent.is_primary === true) {
        const { error: updateError } = await supabase
          .from('parents')
          .update({ is_primary: false })
          .eq('family_id', parent.family_id)
          .eq('is_primary', true);
          
        if (updateError) {
          console.error('Error updating existing primary parent:', updateError);
          // Continue with adding the parent even if update fails
        }
      }

      const { data, error } = await supabase
        .from('parents')
        .insert([parent])
        .select()
        .single();

      if (error) throw error;
      
      return mapToParent(data);
    } catch (error) {
      this.handleApiError(
        error, 
        'Error adding parent', 
        { 
          category: ErrorCategory.DATABASE,
          operation: 'addParent',
          additionalInfo: { parent }
        }
      );
      throw error;
    }
  }

  /**
   * Add a student to a family
   */
  static async addStudent(student: Omit<Student, 'id' | 'created_at'>): Promise<Student> {
    try {
      const { data, error } = await supabase
        .from('students')
        .insert([student])
        .select()
        .single();

      if (error) throw error;
      
      return mapToStudent(data);
    } catch (error) {
      this.handleApiError(
        error, 
        'Error adding student', 
        { 
          category: ErrorCategory.DATABASE,
          operation: 'addStudent',
          additionalInfo: { student }
        }
      );
      throw error;
    }
  }
}

export default FamilyService;
